import React, { useState, useEffect } from 'react';
import { AlertCircle, Users, Calendar, Package, FileText, ArrowRight, Bell } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ActionCard({ icon: Icon, title, description, count, to, color, testId }) {
  return (
    <Link
      to={to}
      className="card hover:shadow-xl transition-all duration-300 group cursor-pointer border-2 hover:border-primary"
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-4">
        <div 
          className="w-16 h-16 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"
          style={{ backgroundColor: `${color}20`, color }}
        >
          <Icon size={32} />
        </div>
        {count !== undefined && (
          <div className="text-right">
            <p className="text-3xl font-bold font-display" data-testid={`${testId}-count`}>
              {count}
            </p>
          </div>
        )}
      </div>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-muted-foreground mb-4">{description}</p>
      <div className="flex items-center text-primary font-medium group-hover:gap-3 transition-all">
        <span>Vai alla sezione</span>
        <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform" />
      </div>
    </Link>
  );
}

function UrgencyBadge({ level, days }) {
  const getBadgeClass = () => {
    if (level === 'expired') return 'badge-critical';
    if (level === 'critical') return 'badge-critical';
    if (level === 'warning') return 'badge-warning';
    if (level === 'soon') return 'badge-soon';
    return 'badge-ok';
  };
  
  const getLabel = () => {
    if (days < 0) return `Scaduto da ${Math.abs(days)} giorni`;
    if (days === 0) return 'Scade oggi';
    if (days === 1) return 'Scade domani';
    return `${days} giorni`;
  };
  
  return (
    <span className={`badge ${getBadgeClass()}`} data-testid="urgency-badge">
      {getLabel()}
    </span>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      
      const statsRes = await fetch(`${API_URL}/api/dashboard/stats`);
      const statsData = await statsRes.json();
      setStats(statsData);
      
      const deadlinesRes = await fetch(`${API_URL}/api/dashboard/upcoming-deadlines?days=30`);
      const deadlinesData = await deadlinesRes.json();
      setUpcomingDeadlines(deadlinesData);
      
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="loading" data-testid="loading">
        <p>Caricamento dashboard...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="empty-state" data-testid="error">
        <AlertCircle className="empty-state-icon" />
        <p>Errore nel caricamento: {error}</p>
      </div>
    );
  }
  
  return (
    <div data-testid="dashboard-page">
      <div className="page-header">
        <h1 className="page-title">Dashboard Life</h1>
        <p className="page-description">
          Benvenuto nel portale di gestione HR e HSE
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <ActionCard
          icon={Users}
          title="Dipendenti"
          description="Gestisci anagrafica e informazioni dipendenti"
          count={stats?.total_employees || 0}
          to="/employees"
          color="hsl(199, 89%, 33%)"
          testId="action-employees"
        />
        
        <ActionCard
          icon={Calendar}
          title="Scadenze"
          description="Monitora visite mediche, corsi e certificazioni"
          count={stats?.total_deadlines || 0}
          to="/scadenze"
          color="hsl(28, 93%, 44%)"
          testId="action-scadenze"
        />
        
        <ActionCard
          icon={Package}
          title="Asset"
          description="Gestisci mezzi e attrezzature aziendali"
          count={stats?.total_assets || 0}
          to="/assets"
          color="hsl(152, 52%, 33%)"
          testId="action-assets"
        />
        
        <ActionCard
          icon={FileText}
          title="Documenti"
          description="Repository centrale documentazione"
          to="/documents"
          color="hsl(174, 44%, 33%)"
          testId="action-documents"
        />
      </div>
      
      <div className="card bg-gradient-to-br from-red-50 to-orange-50 border-red-200">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <Bell size={24} className="text-red-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold mb-1">⚠️ Alert Scadenze Imminenti</h2>
            <p className="text-muted-foreground">
              {stats?.deadlines_7_days || 0} scadenze nei prossimi 7 giorni • {stats?.deadlines_15_days || 0} nei prossimi 15 giorni • {stats?.expired_count || 0} scadute
            </p>
          </div>
          <Link 
            to="/scadenze" 
            className="btn btn-primary whitespace-nowrap" 
            data-testid="view-all-deadlines"
          >
            Vedi Tutte
          </Link>
        </div>
        
        {upcomingDeadlines.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-muted-foreground">Nessuna scadenza imminente</p>
          </div>
        ) : (
          <div className="space-y-3">
            {upcomingDeadlines.slice(0, 5).map((item) => (
              <div 
                key={item._id} 
                className="bg-white rounded-lg p-4 border border-border hover:shadow-md transition-shadow"
                data-testid="alert-item"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="badge badge-secondary text-xs">{item.tipo}</span>
                      <UrgencyBadge 
                        level={item.urgency_level} 
                        days={item.days_remaining}
                      />
                    </div>
                    <h3 className="font-semibold mb-1 truncate">{item.titolo}</h3>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <Link 
                        to={`/employees/${item.employee_id}`}
                        className="text-primary hover:underline font-medium"
                      >
                        {item.employee_nome} {item.employee_cognome}
                      </Link>
                      <span>•</span>
                      <span>Scadenza: {format(new Date(item.data_scadenza), 'dd/MM/yyyy', { locale: it })}</span>
                    </div>
                  </div>
                  <Link 
                    to={`/employees/${item.employee_id}`}
                    className="btn btn-secondary btn-sm flex-shrink-0"
                    data-testid="view-employee"
                  >
                    Dettagli
                  </Link>
                </div>
              </div>
            ))}
            
            {upcomingDeadlines.length > 5 && (
              <div className="text-center pt-2">
                <Link 
                  to="/scadenze" 
                  className="text-primary hover:underline font-medium"
                >
                  Vedi altre {upcomingDeadlines.length - 5} scadenze →
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
